import './charts.css'

/**
 * Графики мини-аппа «Селлер-Штаба». Перенесены без изменения логики из эталона агента «Графики
 * данных» (C:/tmp/rebrand/charts/, спецификация — C:/tmp/rebrand/09-charts.md). Три компонента: Spark (спарклайн), BarList (полосы
 * «эта неделя / прошлая» и рейтинг статей), Meter (мини-шкала доли к цели или порогу).
 *
 * Без библиотек (ci/check-bundle-budget.sh): SVG рисует только геометрию, весь текст — HTML
 * (шрифт Onest, tabular-nums, перенос — средствами вёрстки, без замера текста в JS).
 *
 * ⚠️ [F-4.0.11] Клиент НЕ форматирует числа. `value` — числовая строка сервера ТОЛЬКО для
 * геометрии (Number → доля → проценты ширины). На экран и в aria-label идут готовые строки.
 * ⛔ Здесь запрещено: toFixed / toLocaleString / Intl для печати, вычитание и деление «для подписи»,
 * выбор цвета по знаку числа. Смысл (хорошо, внимание, риск, штраф) приходит полем `tone`.
 */

export type Tone = 'good' | 'attn' | 'risk' | 'fine' | 'neutral'

/** Точка ряда. Порядок — по возрастанию даты; все дни окна; нет данных — value: null. */
export interface SeriesPoint {
  /** Ключ React ('2026-10-06'); не печатается. */
  key: string
  /** Готовая подпись оси от сервера: «вт», «06.10». */
  label: string
  /** Числовая строка ('172340.0000') или число — только для геометрии. */
  value: string | number | null
  /** Готовая строка «172 340 ₽»; null — значит «—». */
  display: string | null
  /** День не закрыт (data_partial / is_preliminary): отрезок пунктиром, точка полая. */
  partial?: boolean
}

const DASH = '—'

/** Строка сервера → число. Только для геометрии: на экран это число не попадает никогда. */
export function num(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** Округление КООРДИНАТЫ, чтобы путь SVG был короче. К печати чисел отношения не имеет. */
const r2 = (v: number): number => Math.round(v * 100) / 100

const TONE_FILL: Record<Tone, string> = {
  good: 'var(--chart-good)', attn: 'var(--chart-attn)', risk: 'var(--chart-risk)',
  fine: 'var(--chart-fine)', neutral: 'var(--chart-neutral)',
}

// ── Spark ────────────────────────────────────────────────────────────────────

export interface SparkProps {
  points: readonly SeriesPoint[]
  /** aria-label — готовая фраза сервера (summary): «Выручка по дням 06–12.10: от 128 960 ₽ (вс) до 181 220 ₽ (ср)». */
  label: string
  /** Прошлый период — серый пунктир. Только для size="lg". */
  compare?: readonly SeriesPoint[]
  /** sm — 32 px в KPI-плитке; lg — 96 px в отчёте, с подписями дней. */
  size?: 'sm' | 'lg'
}

/** Запас сверху и снизу: радиус концевой точки с ореолом. */
const PAD = 5

/** Отрезки ряда: сплошные, пунктирные (неполный день) и одиночные точки между пропусками. */
function trace(
  vals: readonly (number | null)[], pts: readonly SeriesPoint[],
  x: (i: number) => number, y: (v: number) => number,
): { solid: string; dashed: string; dots: string } {
  let solid = ''
  let dashed = ''
  let dots = ''
  vals.forEach((a, i) => {
    if (a === null) return
    const b = i + 1 < vals.length ? vals[i + 1] ?? null : null
    const before = i === 0 ? null : vals[i - 1] ?? null
    // Точка между двумя пропусками: без неё значение на графике пропало бы бесследно.
    if (before === null && b === null) dots += `M${r2(x(i))} ${r2(y(a))}h0.01`
    if (b !== null) {
      const seg = `M${r2(x(i))} ${r2(y(a))}L${r2(x(i + 1))} ${r2(y(b))}`
      if (pts[i]?.partial === true || pts[i + 1]?.partial === true) dashed += seg
      else solid += seg
    }
  })
  return { solid, dashed, dots }
}

export function Spark({ points, label, compare, size = 'sm' }: SparkProps): React.JSX.Element {
  const h = size === 'lg' ? 96 : 32
  const cls = `chart-spark chart-spark--${size}`
  const cur = points.map((p) => num(p.value))
  const known = cur.filter((v): v is number => v !== null)
  // Меньше двух известных точек — линии нет. Место держим, чтобы карточка не прыгала.
  if (known.length < 2) {
    return <div className={cls + ' chart-spark--empty'} role="img" aria-label={label} style={{ height: h }} />
  }
  const prev = (compare ?? []).map((p) => num(p.value))
  const all = known.concat(prev.filter((v): v is number => v !== null))
  // Масштаб «пол — половина максимума»: низ оси = меньшее из минимума ряда и половины максимума.
  // Колебание показано не больше чем вдвое крупнее, чем на оси от нуля (шум на 3 % не выглядит
  // обвалом), а падение на 30 % видно. Значения ниже нуля (маржа) опускают ноль внутрь — нулевая линия.
  const top = Math.max(...all)
  const bottom = Math.min(...all)
  const hi = Math.max(top, 0)
  const lo = bottom < 0 ? bottom : Math.min(bottom, top / 2)
  const xAt = (len: number) => (i: number): number => (len < 2 ? 50 : (i / (len - 1)) * 100)
  const x = xAt(points.length)
  const y = (v: number): number => (hi === lo ? h - PAD : PAD + ((hi - v) / (hi - lo)) * (h - 2 * PAD))
  const main = trace(cur, points, x, y)
  const back = compare ? trace(prev, compare, xAt(compare.length), y) : null
  let last = -1
  cur.forEach((v, i) => { if (v !== null) last = i })
  const end = `M${r2(x(last))} ${r2(y(cur[last] as number))}h0.01`
  return (
    <div className={cls} role="img" aria-label={label}>
      <svg viewBox={`0 0 100 ${h}`} preserveAspectRatio="none" width="100%" height={h}
        aria-hidden="true" focusable="false">
        {lo < 0 && <path className="chart-rule" d={`M0 ${r2(y(0))}H100`} />}
        {back && <path className="chart-line chart-line--prev" d={back.solid + back.dashed} />}
        {main.solid && <path className="chart-line" d={main.solid} />}
        {main.dashed && <path className="chart-line chart-line--partial" d={main.dashed} />}
        {main.dots && <path className="chart-dot" d={main.dots} />}
        <path className="chart-end-halo" d={end} />
        <path className="chart-end" d={end} />
        {points[last]?.partial === true && <path className="chart-end-hole" d={end} />}
      </svg>
      {size === 'lg' && (
        <div className="chart-xlabels" aria-hidden="true">
          {points.map((p, i) => <span key={p.key} style={{ left: `${r2(x(i))}%` }}>{p.label}</span>)}
        </div>
      )}
    </div>
  )
}

// ── BarList ──────────────────────────────────────────────────────────────────

export interface BarRow {
  key: string
  /** «Эта неделя», «Штрафы» — готовая строка. */
  label: string
  /** Уточнение: «05–11.10», «было 22 687 ₽» — готовая строка. */
  sub?: string | null
  /** Только для длины полосы. */
  value: string | number | null
  /** «872 290 ₽»; null — «—». */
  display: string | null
  /** Структурная роль: прошлый период рисуется серым. */
  role?: 'current' | 'previous'
  /** Смысл от сервера (fine — кармин). Без tone текущий ряд — кобальт. */
  tone?: Tone | null
  /** Прошлый период — графитовая риска поверх полосы (только геометрия; подпись — в sub). */
  prev?: string | number | null
}

export interface BarListProps {
  rows: readonly BarRow[]
  /** Название списка для чтения с экрана: «Выплата WB: эта и прошлая неделя». */
  label: string
  /**
   * stack — подпись и значение строкой, полоса под ними (по умолчанию, любые подписи).
   * inline — «подпись | полоса | значение» в одну строку: для 2–3 коротких строк без sub
   * (пара «эта / прошлая» в главной карточке). Без subgrid и уже 360 px сам становится stack.
   */
  layout?: 'stack' | 'inline'
}

export function BarList({ rows, label, layout = 'stack' }: BarListProps): React.JSX.Element {
  const vals: number[] = []
  for (const r of rows) {
    for (const v of [num(r.value), num(r.prev)]) if (v !== null) vals.push(v)
  }
  // Полосы — только от нуля, только для неотрицательных и только когда есть с чем сравнить
  // (два и больше известных значения). Иначе остаются строки текста.
  const bars = vals.length >= 2 && vals.every((v) => v >= 0)
  const max = Math.max(0, ...vals)
  // Ненулевое значение не короче 1 %: иначе 0,4 % неотличимы от настоящего нуля.
  const pct = (v: number): number => (max === 0 || v === 0 ? 0 : r2(Math.max((v / max) * 100, 1)))
  return (
    <ul className={'chart-bars chart-bars--' + layout} aria-label={label}>
      {rows.map((r) => {
        const v = num(r.value)
        const p = num(r.prev)
        const fill = r.tone ? TONE_FILL[r.tone] : r.role === 'previous' ? 'var(--chart-prev)' : 'var(--chart-main)'
        return (
          <li key={r.key} className="chart-bars__row">
            <span className="chart-bars__label">
              {r.label}
              {r.sub ? <span className="chart-bars__sub">{r.sub}</span> : null}
            </span>
            <span className="chart-bars__value">
              {r.display ?? <><span aria-hidden="true">{DASH}</span><span className="chart-sr">нет данных</span></>}
            </span>
            {bars && (
              <svg className="chart-bars__bar" width="100%" height="10" aria-hidden="true" focusable="false">
                {v !== null && v > 0 && <rect y="2" width={`${pct(v)}%`} height="6" rx="2" fill={fill} />}
                {p !== null && (
                  <rect className="chart-bars__prev" x={`${pct(p)}%`} width="2" height="10" transform="translate(-1 0)" />
                )}
              </svg>
            )}
          </li>
        )
      })}
    </ul>
  )
}

// ── Meter ────────────────────────────────────────────────────────────────────

export interface MeterProps {
  /** Только геометрия. */
  value: string | number | null
  /** Правая граница шкалы — задаёт сервер (например, полторы цели). */
  max: string | number | null
  /** Цель или порог — графитовая риска; null — риски нет. */
  target?: string | number | null
  /** Подпись риски — готовая строка: «цель 8,0 %», «поставка 5 дн.». */
  targetCaption?: string | null
  /** Смысл от сервера; без него заливка нейтральная. */
  tone?: Tone | null
  /** aria-label — готовая фраза: «ДРР 3,4 % при цели 8,0 % — в норме». */
  label: string
}

export function Meter(
  { value, max, target, targetCaption, tone, label }: MeterProps,
): React.JSX.Element | null {
  const v = num(value)
  const m = num(max)
  const t = num(target)
  // Шкалу не из чего строить — компонент молчит, родитель показывает текст.
  if (m === null || m <= 0) return null
  const at = (x: number): number => r2(Math.min(Math.max(x / m, 0), 1) * 100)
  const tp = t === null ? null : at(t)
  const edge = tp === null ? '' : tp < 15 ? ' is-start' : tp > 85 ? ' is-end' : ''
  const cap = tp !== null && targetCaption ? targetCaption : null
  return (
    <div className={'chart-meter' + (cap ? ' chart-meter--cap' : '')} role="img" aria-label={label}>
      <svg width="100%" height="16" aria-hidden="true" focusable="false">
        <rect className="chart-meter__track" y="4" width="100%" height="8" rx="4" />
        {v !== null && v > 0 && (
          <rect y="4" width={`${Math.max(at(v), 2)}%`} height="8" rx="4" fill={TONE_FILL[tone ?? 'neutral']} />
        )}
        {/* Значение за краем шкалы: полоса до конца и белый разрыв — «шкала обрезана». */}
        {v !== null && v > m && <rect className="chart-meter__cut" x="94%" y="4" width="3" height="8" />}
        {tp !== null && (
          <rect className="chart-meter__mark" x={`${tp}%`} width="2" height="16" rx="1" transform="translate(-1 0)" />
        )}
      </svg>
      {cap !== null && (
        <span className={'chart-meter__cap' + edge} style={{ left: `${tp}%` }} aria-hidden="true">{cap}</span>
      )}
    </div>
  )
}
